import React, { useState } from "react";

import { useFetchPrintJobsQuery } from "../../../api/printingApi";
import { useLanguage } from "../../../contexts/LanguageContext";
import { Btn, EmptyState, Pill, Segmented, Select, Spinner, Thumb, cx } from "../../catalogProductStudio/edit/StudioUi";
import DismissalHistoryView from "./DismissalHistoryView";
import { fill, formatDateTime, plural } from "../printCenterModel";

export const STATUS_TONES = {
  generated: "neutral",
  awaiting_confirmation: "warning",
  confirmed: "success",
  discarded: "neutral",
};

function PrintHistoryView({ tt, onOpenJob }) {
  const [section, setSection] = useState("prints");
  const sections = (
    <Segmented
      className="pc-history__sections"
      ariaLabel={tt("tab_history")}
      value={section}
      onChange={setSection}
      options={[
        { value: "prints", label: tt("historyPrints") },
        { value: "dismissals", label: tt("historyDismissals") },
      ]}
    />
  );
  if (section === "dismissals") {
    return (
      <div className="pc-history">
        {sections}
        <DismissalHistoryView tt={tt} />
      </div>
    );
  }
  return <PrintJobHistory tt={tt} onOpenJob={onOpenJob} sections={sections} />;
}

function PrintJobHistory({ tt, onOpenJob, sections }) {
  const { language } = useLanguage();
  const [status, setStatus] = useState("");
  const [source, setSource] = useState("");
  const [page, setPage] = useState(1);
  const { data, isLoading, isFetching, isError, refetch } = useFetchPrintJobsQuery({ status, source, page, pageSize: 20 });

  return (
    <div className="pc-history">
      {sections}
      <div className="pc-history__filters">
        <label className="pc-filter">
          <span className="pc-filter__label">{tt("historyStatus")}</span>
          <Select value={status} onChange={(value) => { setStatus(value); setPage(1); }}>
            <option value="">{tt("filterAny")}</option>
            {Object.keys(STATUS_TONES).map((value) => (
              <option key={value} value={value}>{tt(`jobStatus_${value}`)}</option>
            ))}
          </Select>
        </label>
        <label className="pc-filter">
          <span className="pc-filter__label">{tt("historySource")}</span>
          <Select value={source} onChange={(value) => { setSource(value); setPage(1); }}>
            <option value="">{tt("filterAny")}</option>
            {["needs_labels", "catalog", "scan", "reprint"].map((value) => (
              <option key={value} value={value}>{tt(`jobSource_${value}`)}</option>
            ))}
          </Select>
        </label>
        {isFetching ? <Spinner /> : null}
      </div>

      {isLoading ? (
        <div className="pc-loading"><Spinner label={tt("loading")} /></div>
      ) : isError ? (
        <EmptyState icon="alert" title={tt("loadError")} action={<Btn onClick={refetch}>{tt("retry")}</Btn>} />
      ) : !data.items.length ? (
        status || source ? (
          <EmptyState
            icon="search"
            title={tt("historyNoMatchTitle")}
            action={<Btn onClick={() => { setStatus(""); setSource(""); setPage(1); }}>{tt("clearFilters")}</Btn>}
          />
        ) : (
          <EmptyState icon="history" title={tt("historyEmptyTitle")}>{tt("historyEmptyCopy")}</EmptyState>
        )
      ) : (
        <>
          <ul className="pc-jobs">
            {data.items.map((job) => (
              <li key={job.id}>
                <button type="button" className={cx("pc-job", job.status === "awaiting_confirmation" && "is-awaiting")} onClick={() => onOpenJob(job.id)}>
                  <span className="pc-job__thumbs" aria-hidden="true">
                    {(job.imageUrls.length ? job.imageUrls : [null]).map((url, index) => (
                      <Thumb key={`${url}-${index}`} src={url} size={38} />
                    ))}
                  </span>
                  <span className="pc-job__main">
                    <strong>{fill(tt("jobTitle"), { job: job.id })} · {job.productTitles.join(", ")}{job.productCount > job.productTitles.length ? "…" : ""}</strong>
                    <span className="pc-muted pc-small">
                      {formatDateTime(job.createdAt, language)} · {job.createdByName || "—"} · {tt(`jobSource_${job.source}`)}
                      {job.reprintOfJobId ? ` · ${fill(tt("reprintOf"), { job: job.reprintOfJobId })}` : ""}
                    </span>
                  </span>
                  <span className="pc-job__numbers">
                    <strong>{plural(tt, "labelCount", job.totalLabels)}</strong>
                    <span className="pc-muted pc-small">
                      {job.newStockLabels ? `${fill(tt("traySplitNew"), { count: job.newStockLabels })} · ` : ""}
                      {plural(tt, "attemptCount", job.attemptCount)}
                    </span>
                  </span>
                  <Pill tone={STATUS_TONES[job.status] || "neutral"}>{tt(`jobStatus_${job.status}`)}</Pill>
                </button>
              </li>
            ))}
          </ul>
          {data.totalPages > 1 ? (
            <div className="pc-pager">
              <Btn size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>{tt("previous")}</Btn>
              <span className="pc-muted pc-small">{fill(tt("pageOf"), { page, total: data.totalPages })}</span>
              <Btn size="sm" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)}>{tt("next")}</Btn>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default PrintHistoryView;
